import {
  DETECTED_KIND_LABELS,
  type DetectedKind,
  type Entity,
  EXTRACTION_DEPTH_HELP,
  EXTRACTION_DEPTH_LABELS,
  EXTRACTION_DEPTHS,
  type ExtractionDepth,
  LIBRARY_KINDS,
  PIPELINE_STATUS_LABELS,
} from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { BookOpen, FolderOpen, Lock, SearchX, Upload, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { DataBlock, DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { EntityAutocomplete } from '../components/guidance/entity-autocomplete';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { CollectionPanel } from '../components/library/collection-panel';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useEntitySearch } from '../lib/entities';
import {
  type Collection,
  collectionTree,
  formatDate,
  type LibraryDoc,
  type LibraryPage,
  useCollections,
  useLibraryDocs,
} from '../lib/library';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Library (PLAN §5.0, §5.0b, §12.7; task 9b): every uploaded file, whatever was extracted from it, with the
// uploader's folders as collections. Filters: collection (sub-collections included), kind, extraction depth,
// upload date, entity mentioned, and words in the name or text.

export interface LibrarySearch {
  collection?: string;
  q?: string;
  kind?: DetectedKind;
  depth?: ExtractionDepth;
  from?: string;
  to?: string;
  entity?: string;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
const day = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);

export const Route = createFileRoute('/library/')({
  validateSearch: (s: Record<string, unknown>): LibrarySearch => ({
    collection: str(s.collection),
    q: str(s.q),
    kind: LIBRARY_KINDS.includes(s.kind as DetectedKind) ? (s.kind as DetectedKind) : undefined,
    depth: EXTRACTION_DEPTHS.includes(s.depth as ExtractionDepth) ? (s.depth as ExtractionDepth) : undefined,
    from: day(s.from),
    to: day(s.to),
    entity: str(s.entity),
  }),
  component: LibraryPageView,
});

const TOUR = {
  id: 'library',
  steps: [
    {
      target: '[data-tour="collections"]',
      title: 'Collections',
      body: 'Each uploaded folder becomes a collection, nested like the folders. A file in two folders is stored once and listed in both.',
      learnMore: 'library-and-collections',
    },
    {
      target: '[data-tour="library-filters"]',
      title: 'Find any file',
      body: 'Search words in names and text, or narrow by kind, extraction depth, upload date or an entity the text mentions.',
    },
    {
      target: '[data-tour="library-results"]',
      title: 'Everything is searchable',
      body: 'Every file is in the Library and answerable by chat, even when nothing was extracted from it. Open one to raise its extraction depth later.',
      learnMore: 'library-and-collections',
    },
  ],
};

function apiParams(s: LibrarySearch) {
  const p = new URLSearchParams({ limit: '50' });
  for (const [k, v] of Object.entries(s)) if (v) p.set(k, v);
  return p;
}

function LibraryPageView() {
  useScreenTour(TOUR);
  const search = Route.useSearch();
  const canWrite = useCan('content.write');
  const collections = useCollections();
  const params = apiParams(search);
  const list = useLibraryDocs(params);
  const [more, setMore] = useState<LibraryDoc[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const key = params.toString();
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset extra pages when the filters change
  useEffect(() => {
    setMore([]);
    setNext(null);
  }, [key]);
  const cursor = more.length ? next : (list.data?.next ?? null);
  const rows = [...(list.data?.documents ?? []), ...more];
  const all = collections.data?.collections ?? [];
  const current = all.find(c => c.id === search.collection);
  const filtered = !!(search.q || search.kind || search.depth || search.from || search.to || search.entity);

  const loadMore = async () => {
    if (!cursor) return;
    const p = new URLSearchParams(params);
    p.set('after', cursor);
    const res = await api<LibraryPage>(`/library/documents?${p}`);
    setMore(m => [...m, ...res.documents]);
    setNext(res.next);
  };

  return (
    <>
      <PageHeader
        title="Library"
        description="Every uploaded file, whatever was extracted from it: searchable, answerable by chat, organised in collections."
        actions={
          canWrite && (
            <Button size="sm" asChild>
              <Link to="/upload">
                <Upload aria-hidden="true" /> Upload
              </Link>
            </Button>
          )
        }
      />
      <Tip id="library" learnMore="library-and-collections">
        Upload a whole folder to keep its structure: each sub-folder becomes a collection. A file that sits in two folders is stored once
        and appears in both.
      </Tip>
      <div className="mt-4 grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <CollectionNav
          collections={all}
          current={search.collection}
          total={!search.collection && !filtered ? list.data?.total : undefined}
        />
        <div className="min-w-0 space-y-4">
          {current && <CollectionPanel collection={current} all={all} />}
          <Filters search={search} />
          <section aria-label="Documents" data-tour="library-results">
            {list.isError ? (
              <ErrorState error={list.error} title="The Library couldn’t be loaded" onRetry={() => list.refetch()} compact />
            ) : list.isPending && !list.data ? (
              <div className="space-y-2" aria-busy="true">
                <span className="sr-only">Loading…</span>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              filtered ? (
                <EmptyState icon={SearchX} title="No documents match these filters" learnMore="library-and-collections">
                  Try other words, or clear the filters.
                  <div className="mt-3">
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/library" search={{ collection: search.collection }}>
                        Clear filters
                      </Link>
                    </Button>
                  </div>
                </EmptyState>
              ) : (
                <EmptyState
                  icon={BookOpen}
                  title={current ? 'This collection is empty' : 'The Library is empty'}
                  learnMore="library-and-collections"
                  action={
                    canWrite && (
                      <Button size="sm" asChild>
                        <Link to="/upload">
                          <Upload aria-hidden="true" /> Upload files or a folder
                        </Link>
                      </Button>
                    )
                  }
                >
                  Every file you upload lands here, even when nothing is extracted from it.
                </EmptyState>
              )
            ) : (
              <>
                <p className="mb-2 text-sm text-muted-foreground" aria-live="polite">
                  <DataNumber>{list.data?.total ?? rows.length}</DataNumber> {list.data?.total === 1 ? 'document' : 'documents'}
                </p>
                <DocumentsTable rows={rows} />
              </>
            )}
            {cursor && (
              <div className="mt-3 flex justify-center">
                <Button size="sm" variant="outline" onClick={loadMore}>
                  Show more
                </Button>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

function CollectionNav({ collections, current, total }: { collections: Collection[]; current?: string; total?: number }) {
  const tree = collectionTree(collections);
  const item =
    'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted aria-[current=page]:bg-muted aria-[current=page]:font-medium';
  return (
    <nav aria-label="Collections" data-tour="collections" className="self-start rounded-lg border bg-surface p-2">
      <h2 className="px-2 pt-1 pb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">Collections</h2>
      <ul className="space-y-0.5">
        <li>
          <Link to="/library" aria-current={!current ? 'page' : undefined} className={item}>
            <span className="flex items-center gap-2">
              <BookOpen className="size-4 text-muted-foreground" aria-hidden="true" /> All documents
            </span>
            {total !== undefined && <DataNumber className="text-xs text-muted-foreground">{total}</DataNumber>}
          </Link>
        </li>
        {tree.map(({ c, depth }) => (
          <li key={c.id} style={{ paddingInlineStart: `${depth * 0.75}rem` }}>
            <Link to="/library" search={{ collection: c.id }} aria-current={current === c.id ? 'page' : undefined} className={item}>
              <span className="flex min-w-0 items-center gap-2">
                <FolderOpen className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <DataText className="truncate">{c.name}</DataText>
              </span>
              <DataNumber className="text-xs text-muted-foreground" aria-label={`${c.totalCount} documents`}>
                {c.totalCount}
              </DataNumber>
            </Link>
          </li>
        ))}
      </ul>
      {tree.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">Upload a folder to create collections.</p>}
    </nav>
  );
}

function Select({
  label,
  help,
  value,
  onChange,
  options,
  allLabel,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (v: string | undefined) => void;
  options: { value: string; label: string }[];
  allLabel: string;
}) {
  const id = useId();
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-xs font-medium">
          {label}
        </label>
        <FieldHelp label={label} help={help} learnMore="library-and-collections" />
      </div>
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value || undefined)}
        className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
      >
        <option value="">{allLabel}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Filters({ search }: { search: LibrarySearch }) {
  const navigate = useNavigate({ from: '/library/' });
  const set = (patch: Partial<LibrarySearch>) => navigate({ search: s => ({ ...s, ...patch }), replace: true });
  const [q, setQ] = useState(search.q ?? '');
  const qid = useId();
  const fromId = useId();
  const toId = useId();
  const entityId = useId();
  const { data: snap } = useSchema();
  const entitySearch = useEntitySearch(snap);
  const entity = useQuery({
    queryKey: ['entity', search.entity],
    queryFn: () => api<Entity>(`/entities/${encodeURIComponent(search.entity ?? '')}`),
    enabled: !!search.entity,
  });
  useEffect(() => setQ(search.q ?? ''), [search.q]);
  useEffect(() => {
    if ((search.q ?? '') === q.trim()) return;
    const t = setTimeout(() => set({ q: q.trim() || undefined }), 300);
    return () => clearTimeout(t);
  });
  const any = !!(search.q || search.kind || search.depth || search.from || search.to || search.entity);
  return (
    <form
      aria-label="Filter the Library"
      data-tour="library-filters"
      className="grid gap-3 rounded-lg border bg-surface p-3 sm:grid-cols-2 xl:grid-cols-[minmax(12rem,2fr)_repeat(2,minmax(8rem,1fr))_repeat(2,minmax(8rem,10rem))]"
      onSubmit={e => {
        e.preventDefault();
        set({ q: q.trim() || undefined });
      }}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label htmlFor={qid} className="text-xs font-medium">
            Words in name or text
          </label>
          <FieldHelp
            label="Words in name or text"
            help="Finds files whose name contains what you type, or whose text contains every word."
            example="ELNET trip Brussels"
            learnMore="library-and-collections"
          />
        </div>
        <input
          id={qid}
          type="search"
          dir="auto"
          value={q}
          placeholder="e.g. ELNET trip Brussels"
          onChange={e => setQ(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-surface px-3 text-sm placeholder:text-muted-foreground"
        />
      </div>
      <Select
        label="Kind"
        help="What the file is, as detected at upload: profile sheet, transcript, interview synthesis, report, guide…"
        value={search.kind ?? ''}
        onChange={v => set({ kind: v as DetectedKind | undefined })}
        options={LIBRARY_KINDS.map(k => ({ value: k, label: DETECTED_KIND_LABELS[k] }))}
        allLabel="Any kind"
      />
      <Select
        label="Extraction depth"
        help="How much was extracted: Full, Claims & mentions, Mentions only, or Library only (nothing extracted, still searchable)."
        value={search.depth ?? ''}
        onChange={v => set({ depth: v as ExtractionDepth | undefined })}
        options={EXTRACTION_DEPTHS.map(d => ({ value: d, label: EXTRACTION_DEPTH_LABELS[d] }))}
        allLabel="Any depth"
      />
      <div className="space-y-1">
        <label htmlFor={fromId} className="text-xs font-medium">
          Uploaded from
        </label>
        <input
          id={fromId}
          type="date"
          value={search.from ?? ''}
          onChange={e => set({ from: e.target.value || undefined })}
          className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
        />
      </div>
      <div className="space-y-1">
        <label htmlFor={toId} className="text-xs font-medium">
          Uploaded to
        </label>
        <input
          id={toId}
          type="date"
          value={search.to ?? ''}
          onChange={e => set({ to: e.target.value || undefined })}
          className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
        />
      </div>
      <div className="space-y-1 sm:col-span-2">
        <div className="flex items-center gap-1">
          <label htmlFor={entityId} className="text-xs font-medium">
            Mentions entity
          </label>
          <FieldHelp
            label="Mentions entity"
            help="Only files with a passage that mentions this actor, event, place or asset."
            learnMore="library-and-collections"
          />
        </div>
        {search.entity ? (
          <p className="flex h-9 items-center gap-2 text-sm">
            <DataText className="font-medium">{entity.data?.displayName ?? '…'}</DataText>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => set({ entity: undefined })}
              aria-label="Remove the entity filter"
            >
              <X aria-hidden="true" />
            </Button>
          </p>
        ) : (
          <EntityAutocomplete
            id={entityId}
            placeholder="e.g. ELNET"
            search={query => entitySearch(null, query)}
            onSelect={e => set({ entity: e.id })}
          />
        )}
      </div>
      <div className="flex items-end">
        {any && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => navigate({ search: s => ({ collection: s.collection }), replace: true })}
          >
            <X aria-hidden="true" /> Clear
          </Button>
        )}
      </div>
    </form>
  );
}

function DocumentsTable({ rows }: { rows: LibraryDoc[] }) {
  return (
    <div className="relative overflow-x-auto rounded-lg border bg-surface">
      <table className="w-full text-sm">
        <caption className="sr-only">Library documents</caption>
        <thead className="border-b text-start text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 text-start font-medium">
              Document
            </th>
            <th scope="col" className="px-3 py-2 text-start font-medium">
              Kind
            </th>
            <th scope="col" className="px-3 py-2 text-start font-medium">
              Depth
            </th>
            <th scope="col" className="px-3 py-2 text-start font-medium">
              Collections
            </th>
            <th scope="col" className="px-3 py-2 text-start font-medium">
              Status
            </th>
            <th scope="col" className="px-3 py-2 text-start font-medium whitespace-nowrap">
              Uploaded
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map(d => (
            <tr key={d.id} className="align-top">
              <td className="max-w-md px-3 py-2">
                <span className="flex items-center gap-1.5">
                  {d.sensitivity === 'confidential' && (
                    <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-label="Confidential" role="img" />
                  )}
                  <Link to="/library/$id" params={{ id: d.id }} className="font-medium break-all hover:underline">
                    <DataText>{d.title ?? d.filename}</DataText>
                  </Link>
                </span>
                {d.snippet && <DataBlock className="mt-1 line-clamp-2 text-xs text-muted-foreground">{d.snippet}</DataBlock>}
                <span className="text-xs text-muted-foreground">
                  <DataNumber>{d.passages}</DataNumber> {d.passages === 1 ? 'passage' : 'passages'}
                </span>
              </td>
              <td className="px-3 py-2 whitespace-nowrap">{DETECTED_KIND_LABELS[d.kind]}</td>
              <td className="px-3 py-2 whitespace-nowrap" title={EXTRACTION_DEPTH_HELP[d.depth]}>
                {EXTRACTION_DEPTH_LABELS[d.depth]}
              </td>
              <td className="px-3 py-2">
                {d.collections.map(c => (
                  <Link
                    key={c.id}
                    to="/library"
                    search={{ collection: c.id }}
                    className="me-1 mb-1 inline-block rounded bg-muted px-1.5 py-0.5 text-xs hover:underline"
                  >
                    <DataText>{c.name}</DataText>
                  </Link>
                ))}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">{PIPELINE_STATUS_LABELS[d.status]}</td>
              <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{formatDate(d.uploadedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
